/**
 * RtbfRequestsView — admin RTBF management.
 *
 * Per FWA-PWA-033 G0 D5. State machine actions (Aprobar/Rechazar/Cancelar/Ejecutar)
 * gated por status + 2-person rule. Privacy advertencia visible.
 */
import type { RtbfRequest, RtbfStatus } from '@sisrep/schemas';
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card.js';
import { useAuth } from '../../contexts/AuthContext.js';
import { FwaApiError } from '../../lib/api.js';
import {
  useApproveRtbfRequest,
  useCancelRtbfRequest,
  useExecuteRtbfRequest,
  useRejectRtbfRequest,
  useRtbfRequestsList,
} from '../../lib/queries/rtbf.js';

const STATUS_BADGE: Record<RtbfStatus, { label: string; tone: string }> = {
  'pending-approval': { label: 'Pendiente', tone: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Aprobada', tone: 'bg-blue-100 text-blue-800' },
  rejected: { label: 'Rechazada', tone: 'bg-red-100 text-red-800' },
  'soft-deleted': { label: 'Soft-deleted', tone: 'bg-purple-100 text-purple-800' },
  executed: { label: 'Ejecutada', tone: 'bg-gray-200 text-gray-800' },
  cancelled: { label: 'Cancelada', tone: 'bg-gray-100 text-gray-600' },
};

type ActionMode = 'approve' | 'reject' | 'cancel' | 'execute';

interface ActionState {
  mode: ActionMode;
  request: RtbfRequest;
}

export function RtbfRequestsView() {
  const { state } = useAuth();
  const currentUserEmail = state.status === 'authenticated' ? state.user.email : '';

  const [statusFilter, setStatusFilter] = useState<string>('');
  const list = useRtbfRequestsList(statusFilter ? { status: statusFilter } : {});
  const [action, setAction] = useState<ActionState | null>(null);

  return (
    <div className="space-y-4">
      <div
        className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800"
        role="alert"
      >
        Estos requests involucran PII de menores. Confidencial — uso restringido a flujos RTBF
        formales per privacy.md §4.6.
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardBody>
          <label className="flex flex-col gap-1 max-w-xs">
            <span className="text-sm font-medium text-gray-700">Status</span>
            <select
              className="px-3 py-2 border border-gray-300 rounded-md text-base"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Todas</option>
              <option value="pending-approval">Pendiente aprobación</option>
              <option value="soft-deleted">Soft-deleted (24h window)</option>
              <option value="executed">Ejecutadas</option>
              <option value="rejected">Rechazadas</option>
              <option value="cancelled">Canceladas</option>
            </select>
          </label>
        </CardBody>
      </Card>

      {list.isLoading && <div className="text-center py-8 text-gray-600">Cargando…</div>}
      {list.error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700" role="alert">
          Error: {list.error.message}
        </div>
      )}

      {list.data && (
        <Card>
          <CardBody>
            {list.data.data.data.length === 0 ? (
              <p className="text-center py-8 text-gray-500">
                Sin requests para el filtro aplicado.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200">
                  <tr className="text-left">
                    <th className="py-2 font-medium text-gray-700">Created</th>
                    <th className="py-2 font-medium text-gray-700">Niña</th>
                    <th className="py-2 font-medium text-gray-700">Creator</th>
                    <th className="py-2 font-medium text-gray-700">Status</th>
                    <th className="py-2 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.data.data.map((r) => {
                    const status = STATUS_BADGE[r.status];
                    const isCreator = r.created_by.toLowerCase() === currentUserEmail.toLowerCase();
                    return (
                      <tr key={r.entity_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 text-xs text-gray-500">{r.created_at.slice(0, 10)}</td>
                        <td className="py-2 font-mono text-xs">{r.nina_entity_id.slice(0, 10)}…</td>
                        <td className="py-2 text-xs">{r.created_by}</td>
                        <td className="py-2">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs ${status.tone}`}
                          >
                            {status.label}
                          </span>
                        </td>
                        <td className="py-2">
                          <ActionsCell
                            request={r}
                            isCreator={isCreator}
                            onAction={(mode) => setAction({ mode, request: r })}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      )}

      {action && (
        <ActionModal
          action={action}
          onClose={() => setAction(null)}
          onSuccess={() => {
            setAction(null);
            void list.refetch();
          }}
        />
      )}
    </div>
  );
}

function ActionsCell({
  request,
  isCreator,
  onAction,
}: {
  request: RtbfRequest;
  isCreator: boolean;
  onAction: (mode: ActionMode) => void;
}) {
  const status = request.status;
  if (status === 'pending-approval') {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          className="text-xs text-green-700 hover:text-green-900 disabled:text-gray-400"
          onClick={() => onAction('approve')}
          disabled={isCreator}
          title={isCreator ? 'Eres el creador — 2-person rule bloquea aprobar' : ''}
        >
          Aprobar
        </button>
        <button
          type="button"
          className="text-xs text-red-700 hover:text-red-900"
          onClick={() => onAction('reject')}
        >
          Rechazar
        </button>
        <button
          type="button"
          className="text-xs text-gray-600 hover:text-gray-900"
          onClick={() => onAction('cancel')}
        >
          Cancelar
        </button>
      </div>
    );
  }
  if (status === 'soft-deleted') {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          className="text-xs text-orange-700 hover:text-orange-900"
          onClick={() => onAction('execute')}
        >
          Ejecutar
        </button>
        <button
          type="button"
          className="text-xs text-gray-600 hover:text-gray-900 disabled:text-gray-400"
          onClick={() => onAction('cancel')}
          disabled={isCreator}
          title={isCreator ? 'Eres el creador — 2-person rule bloquea cancelar' : ''}
        >
          Cancelar
        </button>
      </div>
    );
  }
  return <span className="text-xs text-gray-400">—</span>;
}

function ActionModal({
  action,
  onClose,
  onSuccess,
}: {
  action: ActionState;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const approve = useApproveRtbfRequest();
  const reject = useRejectRtbfRequest();
  const cancel = useCancelRtbfRequest();
  const execute = useExecuteRtbfRequest();
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string>('');

  const isPending = approve.isPending || reject.isPending || cancel.isPending || execute.isPending;

  const titleMap: Record<ActionMode, string> = {
    approve: 'Aprobar RTBF request',
    reject: 'Rechazar RTBF request',
    cancel: 'Cancelar RTBF request',
    execute: 'Ejecutar RTBF (IRREVERSIBLE)',
  };

  const handleConfirm = () => {
    setError('');
    const id = action.request.entity_id;
    const etag = action.request.version_id;
    const handleErr = (err: Error) => {
      if (err instanceof FwaApiError) {
        setError(err.problem.detail ?? err.problem.title);
      } else {
        setError(err.message);
      }
    };
    if (action.mode === 'approve') {
      approve.mutate(
        { id, payload: reason ? { approval_reason: reason } : {}, etag },
        { onSuccess, onError: handleErr },
      );
    } else if (action.mode === 'reject') {
      if (reason.trim().length < 10) {
        setError('Motivo de rechazo requerido (mín 10 chars)');
        return;
      }
      reject.mutate(
        { id, payload: { rejection_reason: reason }, etag },
        { onSuccess, onError: handleErr },
      );
    } else if (action.mode === 'cancel') {
      cancel.mutate(
        { id, payload: reason ? { cancellation_reason: reason } : {}, etag },
        { onSuccess, onError: handleErr },
      );
    } else {
      execute.mutate({ id, payload: {}, etag }, { onSuccess, onError: handleErr });
    }
  };

  const needsReasonRequired = action.mode === 'reject';
  const showReasonField = action.mode !== 'execute';

  return (
    <dialog
      open
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-w-none w-full h-full"
      aria-labelledby="rtbf-action-title"
    >
      <Card className="max-w-md mx-4">
        <CardHeader>
          <CardTitle>
            <span id="rtbf-action-title">{titleMap[action.mode]}</span>
          </CardTitle>
        </CardHeader>
        <CardBody>
          {action.mode === 'execute' && (
            <p className="text-sm text-red-700 mb-3 font-semibold">
              Esta acción es IRREVERSIBLE. Aplica hard-delete tier Crítico + pseudonimización tier
              Alto + tombstone tier Medio.
            </p>
          )}
          {showReasonField && (
            <label className="flex flex-col gap-1 mb-3">
              <span className="text-sm font-medium text-gray-700">
                Motivo {needsReasonRequired ? '(requerido, mín 10 chars)' : '(opcional)'}
              </span>
              <p className="text-xs text-amber-700">
                Este texto se almacena en backend. NO incluyas PII (nombres, emails).
              </p>
              <textarea
                className="px-3 py-2 border border-gray-300 rounded-md"
                rows={3}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          )}
          {error && (
            <div
              className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}
          <div className="flex gap-3 mt-4">
            <Button variant="primary" onClick={handleConfirm} disabled={isPending}>
              {isPending ? 'Procesando…' : 'Confirmar'}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
          </div>
        </CardBody>
      </Card>
    </dialog>
  );
}
